/**
 * The Family Builder's client — the agent's `/family-builder` routes (#255),
 * ported from `bimgo-ai-web`'s `familyAuthoringService.ts`.
 *
 * Two changes from v2, both the architecture's:
 *
 * - **The instance, not a version.** v2 sent `revitVersion` and the one agent
 *   talked to the one Revit. Here several Revits can be running, so every request
 *   names the selected one by `target_session_id` and the agent reads its version
 *   off the instance's manifest. The web never sends a version.
 * - **The agent owns the thread.** v2's browser created the conversation on
 *   api-v2 and then told the agent about it. Here the agent creates it on the
 *   first turn (as Edit-with-AI does) and says so with `thread_info`, so a turn
 *   can never leave a thread the artifact does not know about.
 *
 * Every request goes through `agentClient`, with the bearer and
 * `X-Organization-Id` the other agent routes take (`lib/documentContext.ts`). The
 * turn is the chat's own reader (`streamAsk`): the same NDJSON, the same steps,
 * reasoning and abort handling, plus one event of the family's own —
 * `family_build_evidence`, read off the raw events.
 */
import { agentClient, type AgentClient } from "../lib/agentClient";
import { bearer } from "../lib/accessToken";
import { AskError, streamAsk, type AskCallbacks } from "../lib/chat";
import type { Attachment } from "../lib/attachments";
import { FamilyBuilderError, readFamilyError } from "./errors";
import { FAMILY_THREAD_TITLE, readBuildEvidence } from "./model";
import type {
  FamilyArtifactList,
  FamilyArtifactMetadata,
  FamilyBuildEvidence,
  FamilyExecutionResponse,
  FamilyRfaBinding,
  FamilyVersionList,
  FamilyView,
} from "./types";

/** Who asks, and about which Revit. */
export interface FamilyScope {
  token: string;
  orgId: string;
  /** The Revit instance's `sessionId` — the title bar's pick. */
  targetSessionId: string;
}

export const FAMILY_BUILDER_PATH = "/family-builder";

export function artifactPath(artifactId: string, rest = ""): string {
  return `${FAMILY_BUILDER_PATH}/artifacts/${encodeURIComponent(artifactId)}${rest}`;
}

export interface FamilyBuilderClient {
  list(): Promise<FamilyArtifactList>;
  create(input?: { name?: string; description?: string }): Promise<FamilyView>;
  view(artifactId: string): Promise<FamilyView>;
  setThread(artifactId: string, threadId: string | null): Promise<void>;
  activate(artifactId: string): Promise<FamilyRfaBinding>;
  locate(artifactId: string): Promise<FamilyRfaBinding>;
  execute(artifactId: string, parameters: Record<string, unknown>): Promise<FamilyExecutionResponse>;
  versions(artifactId: string): Promise<FamilyVersionList>;
  restore(artifactId: string, version: number): Promise<FamilyView>;
}

export function createFamilyBuilderClient(scope: FamilyScope, client: AgentClient = agentClient): FamilyBuilderClient {
  async function request<T>(method: "GET" | "POST" | "PUT", path: string, body?: Record<string, unknown>): Promise<T> {
    // GETs carry the instance in the query, writes in the body — the agent reads it from either.
    const url =
      method === "GET"
        ? `${path}${path.includes("?") ? "&" : "?"}${new URLSearchParams({ target_session_id: scope.targetSessionId })}`
        : path;
    const response = await client.fetch(url, {
      method,
      headers: {
        Authorization: bearer(scope.token),
        "X-Organization-Id": scope.orgId,
        ...(method === "GET" ? {} : { "Content-Type": "application/json" }),
      },
      ...(method === "GET" ? {} : { body: JSON.stringify({ target_session_id: scope.targetSessionId, ...(body ?? {}) }) }),
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) throw readFamilyError(response.status, payload, response.statusText);
    return payload as T;
  }

  return {
    async list() {
      const data = await request<Partial<FamilyArtifactList>>("GET", `${FAMILY_BUILDER_PATH}/artifacts`);
      return {
        revitVersion: typeof data?.revitVersion === "string" ? data.revitVersion : null,
        artifacts: Array.isArray(data?.artifacts) ? (data.artifacts as FamilyArtifactMetadata[]) : [],
      };
    },
    async create(input = {}) {
      const data = await request<{ artifact?: FamilyView }>("POST", `${FAMILY_BUILDER_PATH}/artifacts`, {
        ...(input.name ? { name: input.name } : {}),
        ...(input.description ? { description: input.description } : {}),
      });
      if (!data?.artifact) throw new Error("Unable to create a Revit family.");
      return data.artifact;
    },
    async view(artifactId) {
      const data = await request<{ view?: FamilyView }>("GET", artifactPath(artifactId, "/view"));
      if (!data?.view || typeof data.view !== "object") throw new Error("The Family Builder returned an invalid family view.");
      return data.view;
    },
    async setThread(artifactId, threadId) {
      await request("PUT", artifactPath(artifactId, "/thread"), { threadId, title: FAMILY_THREAD_TITLE });
    },
    async activate(artifactId) {
      const data = await request<{ binding?: unknown }>("POST", artifactPath(artifactId, "/activate"));
      return readBinding(data?.binding, artifactId);
    },
    async locate(artifactId) {
      const data = await request<{ binding?: unknown }>("POST", artifactPath(artifactId, "/locate"));
      return readBinding(data?.binding, artifactId);
    },
    execute(artifactId, parameters) {
      return request<FamilyExecutionResponse>("POST", artifactPath(artifactId, "/execute"), { parameters });
    },
    async versions(artifactId) {
      const data = await request<Partial<FamilyVersionList>>("GET", artifactPath(artifactId, "/versions"));
      return {
        ...(typeof data?.artifact_id === "string" ? { artifact_id: data.artifact_id } : {}),
        current_version: typeof data?.current_version === "number" ? data.current_version : 0,
        versions: Array.isArray(data?.versions) ? data.versions : [],
      };
    },
    async restore(artifactId, version) {
      const data = await request<{ view?: FamilyView }>("POST", artifactPath(artifactId, `/restore/${version}`));
      if (!data?.view) throw new Error(`Unable to restore version v${version}.`);
      return data.view;
    },
  };
}

/** v2's `readRfaBindingResponse`: a binding for another artifact, or with no path, is not one. */
export function readBinding(value: unknown, artifactId: string): FamilyRfaBinding {
  const binding = value as Partial<FamilyRfaBinding> | null;
  if (
    !binding ||
    typeof binding !== "object" ||
    binding.artifactId !== artifactId ||
    typeof binding.documentPath !== "string" ||
    !binding.documentPath.trim() ||
    !["user", "managed"].includes(binding.ownership ?? "") ||
    typeof binding.activationMode !== "string" ||
    (binding.viewFocusSucceeded !== undefined && typeof binding.viewFocusSucceeded !== "boolean") ||
    (binding.viewFocusMode !== undefined && typeof binding.viewFocusMode !== "string") ||
    [binding.viewFocusError, binding.authoringViewName].some((field) => field != null && typeof field !== "string")
  ) {
    throw new Error("The Family Builder returned an invalid RFA opening response. Revit may already have opened the document.");
  }
  return binding as FamilyRfaBinding;
}

// --- the turn ------------------------------------------------------------------

export interface FamilyTurnParams {
  artifactId: string;
  targetSessionId: string;
  organizationId: string;
  accessToken: string;
  prompt: string;
  projectId?: string | null;
  userId?: string | null;
  attachments?: Attachment[];
  llmProvider?: string;
  llmModel?: string;
  reasoningEffort?: string;
  abortSignal?: AbortSignal;
}

export interface FamilyTurnCallbacks extends AskCallbacks {
  /** A Build finished: its screenshot and geometry, after `build_family`'s `tool_end`. */
  onBuildEvidence?: (evidence: FamilyBuildEvidence) => void;
}

/** The request body of `POST /family-builder/artifacts/:id/chat`. */
export function familyTurnBody(params: FamilyTurnParams): Record<string, unknown> {
  return {
    target_session_id: params.targetSessionId,
    prompt: params.prompt,
    organization_id: params.organizationId,
    ...(params.projectId ? { project_id: params.projectId } : {}),
    ...(params.userId ? { user_id: params.userId } : {}),
    ...(params.attachments && params.attachments.length > 0 ? { attachments: params.attachments } : {}),
    ...(params.llmProvider && params.llmModel ? { llm_provider: params.llmProvider, llm_model: params.llmModel } : {}),
    ...(params.reasoningEffort ? { reasoning_effort: params.reasoningEffort } : {}),
  };
}

/**
 * One family turn. Resolves when the stream ends (a stopped turn resolves, as
 * chat's does); rejects with a `FamilyBuilderError` carrying the agent's own code
 * when the turn is refused — a `FAMILY_RFA_*` refusal has to reach the RFA banner
 * with its `retryAction`, which the chat's error taxonomy would flatten away.
 */
export async function streamFamilyTurn(
  params: FamilyTurnParams,
  callbacks: FamilyTurnCallbacks,
  deps: { client?: AgentClient } = {},
): Promise<void> {
  const base = deps.client ?? agentClient;
  let refused: unknown = undefined;
  const client: AgentClient = {
    baseUrl: () => base.baseUrl(),
    async fetch(path, init) {
      const response = await base.fetch(path, init);
      if (!response.ok) refused = await response.clone().json().catch(() => null);
      return response;
    },
  };
  try {
    await streamAsk(
      {
        path: artifactPath(params.artifactId, "/chat"),
        body: familyTurnBody(params),
        accessToken: params.accessToken,
        abortSignal: params.abortSignal,
        organizationId: params.organizationId,
      },
      {
        ...callbacks,
        onRawEvent: (data) => {
          callbacks.onRawEvent?.(data);
          const evidence = readBuildEvidence(data);
          if (evidence) callbacks.onBuildEvidence?.(evidence);
        },
      },
      { client },
    );
  } catch (error) {
    if (error instanceof AskError && error.status > 0) {
      throw readFamilyError(error.status, refused, error.message);
    }
    throw error;
  }
}

export { FamilyBuilderError };
